import { useRecurso } from './consultas';
import { useSesion } from './sesion';

interface EstadoDelEnlace {
  ultimoReporteEn: string | null;
  fresco: boolean;
}

export interface QueSeSabeDeLosAparatos {
  /** Si el estado en pantalla se puede leer como «así están ahora». */
  sabeLatidos: boolean;
  /** Cuándo reportó la caseta, para poder decirlo. Nulo si nunca. */
  ultimoReporteEn: string | null;
  /** En la caseta esto no aplica y no hay nada que explicar. */
  esNube: boolean;
}

/**
 * Si lo que se ve del estado de los aparatos es de ahora.
 *
 * ## Las tres situaciones, que no son dos
 *
 * En la **caseta** el latido llega por MQTT y lo que se ve es de este segundo.
 * No hay nada que preguntar.
 *
 * En la **nube** el estado sube en fotos cada minuto, así que hay dos casos muy
 * distintos que es fácil confundir en uno:
 *
 *   · **Reporte reciente** — lo que se ve es real, con un minuto de retraso
 *     como mucho. Se enseña igual que en la caseta.
 *   · **Sin reporte, o viejo** — el enlace se cortó, o esta caseta todavía no
 *     está enlazada. Los últimos datos recibidos se quedaron quietos, y
 *     enseñarlos sería decir que once aparatos están caídos cuando en la reja
 *     están funcionando.
 *
 * Ese segundo caso es el que motivó todo esto. «No lo sé» pintado como «está
 * caído» es una alarma falsa **y permanente**, en la pantalla que existe para
 * avisar de las verdaderas — la que después nadie mira.
 */
export function useQueSeSabeDeLosAparatos(): QueSeSabeDeLosAparatos {
  const { rolServidor } = useSesion();
  const esNube = rolServidor === 'nube';

  // `null` no consulta: en la caseta la respuesta se sabe sin preguntar, y esa
  // ruta ni siquiera existe ahí.
  const { datos } = useRecurso<EstadoDelEnlace>(esNube ? '/enlace/estado' : null);

  if (!esNube) return { sabeLatidos: true, ultimoReporteEn: null, esNube: false };

  return {
    // Mientras la respuesta no llegue se asume que no se sabe. Es la respuesta
    // prudente: enseñar «caído» un instante y corregir asusta a quien lo vea.
    sabeLatidos: datos?.fresco ?? false,
    ultimoReporteEn: datos?.ultimoReporteEn ?? null,
    esNube: true,
  };
}

/** «hace 3 minutos», para decir de cuándo son los datos que se están viendo. */
export function haceCuanto(iso: string | null): string | null {
  if (!iso) return null;
  const segundos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (segundos < 90) return 'hace menos de un minuto';
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `hace ${minutos} minutos`;
  const horas = Math.round(minutos / 60);
  if (horas < 48) return `hace ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  return `hace ${Math.round(horas / 24)} días`;
}
